import { useCallback, useEffect, useState } from 'react'
import type { CompetitionRoundView, CompetitionRoute } from './types'

/** The competition's routes. Competition sets are small, so there is no paging. */
export function useCompetitionRoutes(
  competitionId: string | undefined,
  apiUrl: string,
  getAccessTokenSilently: () => Promise<string>,
  enabled = true,
) {
  const [routes, setRoutes] = useState<CompetitionRoute[]>([])
  const [rounds, setRounds] = useState<CompetitionRoundView[]>([])
  const [loading, setLoading] = useState(true)

  const reload = useCallback(async () => {
    if (!competitionId || !enabled) return
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-competitions/${competitionId}/routes`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const body = (await res.json()) as {
          routes: CompetitionRoute[]
          rounds?: CompetitionRoundView[]
        }
        setRoutes(body.routes)
        setRounds(body.rounds ?? [])
      }
    } finally {
      setLoading(false)
    }
  }, [competitionId, apiUrl, enabled, getAccessTokenSilently])

  useEffect(() => {
    void reload()
  }, [reload])

  // Local, optimistic updates so logging feels instant; the sheet has already
  // written to the server by the time these run.
  const applyAscent = useCallback((routeId: string, attempts: number | null) => {
    setRoutes((prev) =>
      prev.map((r) =>
        r.id === routeId ? { ...r, has_ascent: true, attempts: attempts ?? r.attempts } : r,
      ),
    )
  }, [])

  const removeAscent = useCallback((routeId: string) => {
    setRoutes((prev) =>
      prev.map((r) => (r.id === routeId ? { ...r, has_ascent: false, attempts: null } : r)),
    )
  }, [])

  return { routes, rounds, loading, reload, applyAscent, removeAscent }
}
